import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ConorbitLoader } from "@/components/ui/ConorbitLoader";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { useToast } from "@/hooks/use-toast";
import { Copy } from "lucide-react";
import { JournalistOption } from "./types";
import { InvitationLettersTab } from "./InvitationLettersTab";
import { PendingRequestsTab } from "./PendingRequestsTab";
import { PressPassesTab } from "./PressPassesTab";
import { RestrictionsTab } from "./RestrictionsTab";

export function AccreditationView() {
  const { orgId, can, loading: permLoading } = useOrgPermissions();
  const { toast } = useToast();
  const [journalists, setJournalists] = useState<JournalistOption[]>([]);
  const [orgSlug, setOrgSlug] = useState<string | null>(null);
  const [commsEmail, setCommsEmail] = useState("communication@parl.ecowas.int");
  const [currentUserName, setCurrentUserName] = useState("");
  const [currentUserTitle, setCurrentUserTitle] = useState("");
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState("letters");
  const [passPreselect, setPassPreselect] = useState<string | undefined>(undefined);

  const canView = can("accreditation", "view");
  const canEdit = can("accreditation", "edit");

  useEffect(() => {
    if (!orgId) return;

    async function load() {
      setLoadingData(true);
      try {
        // Load journalists with their primary outlet
        const { data: rawJournalists } = await supabase
          .from("journalists" as any)
          .select("id, org_id, first_name, last_name, email, gender, nationality")
          .eq("org_id", orgId)
          .eq("status", "active")
          .order("last_name");

        // Load journalist primary affiliations (outlet names)
        const { data: affiliations } = await supabase
          .from("journalist_affiliations" as any)
          .select("journalist_id, outlet_id, media_outlets(name)")
          .eq("org_id", orgId)
          .eq("is_primary", true);

        const affiliationMap = new Map<string, { outlet_name: string | null; outlet_id: string | null }>();
        (affiliations ?? []).forEach((a: any) => {
          affiliationMap.set(a.journalist_id, {
            outlet_name: a.media_outlets?.name ?? null,
            outlet_id: a.outlet_id ?? null,
          });
        });

        const enrichedJournalists: JournalistOption[] = (rawJournalists ?? []).map(
          (j: any) => ({
            id: j.id,
            org_id: j.org_id,
            first_name: j.first_name,
            last_name: j.last_name,
            email: j.email,
            gender: j.gender,
            nationality: j.nationality,
            outlet_name: affiliationMap.get(j.id)?.outlet_name ?? null,
            outlet_id: affiliationMap.get(j.id)?.outlet_id ?? null,
          })
        );
        setJournalists(enrichedJournalists);

        // Load org comms_email
        const { data: org } = await supabase
          .from("organizations")
          .select("comms_email, slug")
          .eq("id", orgId)
          .single();
        if (org?.comms_email) setCommsEmail(org.comms_email);
        if (org?.slug) setOrgSlug(org.slug);

        // Load current user's name + role title
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("full_name")
            .eq("user_id", user.id)
            .single();
          if (profile?.full_name) setCurrentUserName(profile.full_name);

          const { data: member } = await supabase
            .from("organization_members")
            .select("roles(name)")
            .eq("org_id", orgId)
            .eq("user_id", user.id)
            .eq("status", "active")
            .single();
          if (member) {
            const roleName = (member as any).roles?.name ?? "";
            setCurrentUserTitle(roleName);
          }
        }
      } finally {
        setLoadingData(false);
      }
    }

    load();
  }, [orgId]);

  if (permLoading || loadingData) return <ConorbitLoader variant="inline" />;

  if (!orgId) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          You are not a member of any organisation.
        </CardContent>
      </Card>
    );
  }

  if (!canView) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          You do not have permission to access Accreditation.
        </CardContent>
      </Card>
    );
  }

  function handleRequestPassForJournalist(journalistId: string) {
    setPassPreselect(journalistId);
    setActiveTab("passes");
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Accreditation</h1>
          <p className="text-justify text-muted-foreground">
            Manage invitation letters, accreditation requests, and press passes.
          </p>
        </div>
        {orgSlug && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const url = `${window.location.origin}/${orgSlug}/accreditation-check`;
              navigator.clipboard.writeText(url);
              toast({ title: "Public accreditation check link copied" });
            }}
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy public check link
          </Button>
        )}
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v);
          if (v !== "passes") setPassPreselect(undefined);
        }}
      >
        <TabsList>
          <TabsTrigger value="letters">Invitation Letters</TabsTrigger>
          <TabsTrigger value="requests">Pending Requests</TabsTrigger>
          <TabsTrigger value="passes">Press Passes</TabsTrigger>
          <TabsTrigger value="restrictions">Restrictions</TabsTrigger>
        </TabsList>

        <TabsContent value="letters" className="mt-6">
          <InvitationLettersTab
            orgId={orgId}
            journalists={journalists}
            commsEmail={commsEmail}
            currentUserName={currentUserName}
            currentUserTitle={currentUserTitle}
          />
        </TabsContent>

        <TabsContent value="requests" className="mt-6">
          <PendingRequestsTab
            orgId={orgId}
            journalists={journalists}
            onRequestPassForJournalist={handleRequestPassForJournalist}
          />
        </TabsContent>

        <TabsContent value="passes" className="mt-6">
          <PressPassesTab
            orgId={orgId}
            journalists={journalists}
            preselectedJournalistId={passPreselect}
            onPassIssued={() => setPassPreselect(undefined)}
          />
        </TabsContent>

        <TabsContent value="restrictions" className="mt-6">
          <RestrictionsTab orgId={orgId} canEdit={canEdit} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
